<template>
    <el-dialog v-model="visible" width="390" max-width="550px" custom-class="login-dialog" :close-on-click-modal="false">
        <template #header>
            <div class="dialog-header">
                <span class="dialog-title">登录</span>
                <el-button class="close-btn" icon="el-icon-close" @click="closeDialog" />
            </div>
        </template>

        <div class="login-content">
            <el-input placeholder="请输入邮箱" v-model="email" prefix-icon="el-icon-message">
                 <template #prefix >
                    <img src="@/assets/email.svg" alt="email" class="input-icon" />
                </template>
            </el-input>
            <el-input placeholder="请输入密码" v-model="password" type="password" show-password>
                <template #prefix>
                    <img src="@/assets/lock.svg" alt="lock" class="input-icon" />
                </template>
            </el-input>
        </div>

        <el-button type="primary" class="login-btn" @click="login">登录</el-button>

        <div class="extra-options">
            <el-checkbox v-model="rememberMe">十天内免登录</el-checkbox>
            <a href="#" class="forget-password">忘记密码?</a>
            <!-- <a href="#" class="register-link">去注册</a> -->
        </div>

        <!-- <div class="footer">
            <el-button class="qr-login-btn" icon="el-icon-mobile">
                手机扫码，安全登录
            </el-button>
            <div class="social-login">
                <i class="el-icon-chat-dot-square social-icon"></i>
                <i class="el-icon-chat-line-square social-icon"></i>
                <i class="el-icon-share social-icon"></i>
            </div>
        </div> -->
    </el-dialog>
</template>

<script lang="ts" setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import { UserInfo } from '@/types/user/UserInfo';
import { PublicKeyResponse } from '@/types/user/response/PublicKeyResponse';
import { ApiResponse } from '@/types/ApiResponse';
import { LoginResponse } from '@/types/user/response/LoginResponse';
import { UserLoginRequest } from '@/types/user/request/UserLoginRequest';
import { userService } from "@/services/UserService";
import JSEncrypt from 'jsencrypt';
import { ElMessage }  from 'element-plus'
// 定义 props 和 emits
const props = defineProps({
    visible: {
        type: Boolean,
        required: true,
    },
});

const emit = defineEmits<{
    (event: 'update:visible', value: boolean): void;
    (event: 'login-success', userInfo: UserInfo): void;
}>();

// 控制弹窗显示
const visible = ref(props.visible);

// 监听 props.visible 的变化
watch(() => props.visible, (newVal) => {
    visible.value = newVal;
});

// 监听 visible 的变化，通知父组件
watch(visible, (newVal) => {
    emit('update:visible', newVal);
});

// 表单数据
const email = ref('');
const password = ref('');
const rememberMe = ref(false);

// 登录请求数据
const loginRequest = ref<UserLoginRequest>({
    type: 'email', // 固定为邮箱登录
    password: '',
    email: '',
});

const RASResponse = ref<ApiResponse<PublicKeyResponse>>();
const user = ref<ApiResponse<LoginResponse>>();
const userInfo = ref<UserInfo>();

// 登录方法
const login = async () => {
    loginRequest.value.email = email.value;
    loginRequest.value.password = password.value;

    // 获取公钥用于加密
    const response = await userService.getPublicKey();
    RASResponse.value = response;
    loginRequest.value.key_id = RASResponse.value.data.key_id;

    // 对密码加密
    const encryptpassword = encryptPassword(loginRequest.value.password);
    if (!encryptpassword) {
        return;
    }
    loginRequest.value.password = encryptpassword;

    // 发起登录请求
    const loginResponse = await userService.userlogin(loginRequest.value);
    user.value = loginResponse;

    // 如果登录成功，则存储到 localStorage
    if (user.value.code === 200) {
        localStorage.setItem('token', JSON.stringify(user.value.data.token));
        localStorage.setItem('user', JSON.stringify(user.value.data.data));
        userInfo.value = JSON.parse(JSON.stringify(user.value.data.data));
        // alert(userInfo.value)
        // 触发登录成功事件
        emit('login-success', userInfo.value as UserInfo);
        visible.value = false; // 关闭弹窗
    } else if (user.value.code === 1002) {
        ElMessage({
            message: "账号密码错误，如果忘记，请联系管理员",
            type: "error",
            duration:3000
        })
            
    }
};

// 加密方法
const encryptPassword = (password: string) => {
    const encrypt = new JSEncrypt();
    if (RASResponse.value?.data.public_key) {
        encrypt.setPublicKey(RASResponse.value.data.public_key);
        return encrypt.encrypt(password);
    }
    return false;
};

// 关闭弹窗
const closeDialog = () => {
    visible.value = false;
};
</script>

<style scoped>
.login-dialog {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

:deep(.el-input__prefix) {
    display: flex;
    align-items: center;
    /* 垂直居中 */
    height: 100%;
    /* 高度与输入框一致 */
    padding-left: 12px;
    /* 左侧内边距 */
}

.dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid #e0e0e0;
}

.dialog-title {
    font-size: 20px;
    font-weight: 600;
    color: #333;
}

.close-btn {
    padding: 0;
    background: transparent;
    border: none;
    font-size: 20px;
    color: #909399;
    transition: color 0.3s ease;
}

.close-btn:hover {
    color: #666;
}

.login-content {
    margin-bottom: 20px;
}

.el-input {
    margin-bottom: 16px;
}

:deep(.el-input__inner) {
    height: 44px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    transition: border-color 0.3s ease;
}

:deep(.el-input__inner:focus) {
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.input-icon {
    width: 16px;
    /* 图标宽度 */
    height: 16px;
    /* 图标高度 */
    margin-right: 8px;
    /* 图标与输入框内容的间距 */
    color: #999;
    /* 图标颜色（如果 SVG 支持 currentColor） */
    align-self: center;
    /* 确保图标垂直居中 */
}

.login-btn {
    width: 100%;
    height: 44px;
    background-color: #409eff;
    border-color: #409eff;
    font-size: 16px;
    font-weight: 500;
    border-radius: 8px;
    transition: background-color 0.3s ease;
}

.login-btn:hover {
    background-color: #66b1ff;
}

.extra-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 16px 0;
    font-size: 14px;
}

.forget-password,
.register-link {
    color: #409eff;
    text-decoration: none;
    transition: color 0.3s ease;
}

.forget-password:hover,
.register-link:hover {
    color: #66b1ff;
}

.footer {
    margin-top: 20px;
    text-align: center;
}

.qr-login-btn {
    width: 100%;
    height: 44px;
    border: 1px solid #409eff;
    color: #409eff;
    background-color: transparent;
    border-radius: 8px;
    transition: background-color 0.3s ease;
}

.qr-login-btn:hover {
    background-color: rgba(64, 158, 255, 0.1);
}

.social-login {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-top: 16px;
}

.social-icon {
    font-size: 24px;
    color: #666;
    cursor: pointer;
    transition: color 0.3s ease;
}

.social-icon:hover {
    color: #409eff;
}

/* 响应式布局 */
@media (max-width: 768px) {

    /* 手机端 */
    .login-dialog {
        width: 90% !important;
        /* 缩小弹窗宽度 */
        max-width: 390px;
        /* 最大宽度 */
    }

    .dialog-title {
        font-size: 18px;
        /* 缩小标题字体 */
    }

    .el-input {
        margin-bottom: 12px;
        /* 缩小输入框间距 */
    }

    :deep(.el-input__inner) {
        height: 40px;
        /* 缩小输入框高度 */
        font-size: 14px;
        /* 缩小输入框字体 */
    }

    .login-btn {
        height: 40px;
        /* 缩小按钮高度 */
        font-size: 14px;
        /* 缩小按钮字体 */
    }

    .extra-options {
        font-size: 12px;
        /* 缩小额外选项字体 */
    }

    .input-icon {
        width: 14px;
        /* 缩小图标 */
        height: 14px;
    }
}

@media (min-width: 769px) and (max-width: 1024px) {

    /* 平板端 */
    .login-dialog {
        width: 70% !important;
        /* 缩小弹窗宽度 */
        max-width: 450px;
        /* 最大宽度 */
    }

    .dialog-title {
        font-size: 20px;
        /* 恢复标题字体 */
    }

    .el-input {
        margin-bottom: 16px;
        /* 恢复输入框间距 */
    }

    :deep(.el-input__inner) {
        height: 44px;
        /* 恢复输入框高度 */
        font-size: 16px;
        /* 恢复输入框字体 */
    }

    .login-btn {
        height: 44px;
        /* 恢复按钮高度 */
        font-size: 16px;
        /* 恢复按钮字体 */
    }

    .extra-options {
        font-size: 14px;
        /* 恢复额外选项字体 */
    }

    .input-icon {
        width: 16px;
        /* 恢复图标 */
        height: 16px;
    }
}

@media (min-width: 1025px) {

    /* PC 端 */
    .login-dialog {
        width: 550px;
        max-width: 550px;
        /* 恢复弹窗宽度 */
    }
}</style>